import React from "react";
import { Box, LinearProgress } from "@mui/material";
import { useNow } from "../now";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChairOffice } from "@fortawesome/pro-solid-svg-icons";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import ResourceExtraInfo from "./ResourceExtraInfo";
import * as f from "../../utils/formatter";
import * as d from "shared/src/date.mjs";
import { duration, msToM } from "./fn.mjs";
import { appointmentStep } from "shared/src/appointment.mjs";

/**
 * @param {Appointment} event
 *
 * @returns {Date | null}
 */
function getFieldChangedAt(event, field, to = null) {
  const data = event.audit.data;

  const pred =
    to == null
      ? (a) => a.changed_fields?.[field] != null
      : (a) => a.changed_fields?.[field] === to;

  const last = data.findLast(pred);
  if (last != null) {
    return d.parseUtcDateTime(last.created_at);
  }

  const first = data[0];
  if (
    to == null
      ? first?.row_data?.[field] != null
      : first?.row_data?.[field] === to
  ) {
    return d.parseUtcDateTime(first.created_at);
  }
}

/**
 * @param {Appointment} appointment
 */
function realDuration(appointment) {
  const real_duration = appointment?.service?.locationService?.real_duration;
  if (real_duration != null) {
    return real_duration * 60 * 1000;
  }

  return (
    duration(
      d.parseDateTime(appointment.start),
      d.parseDateTime(appointment.end),
    ) *
    60 *
    1000
  );
}

function StatusSince({ appointments, setChangeEmployee }) {
  const now = useNow();
  const changedAt = getFieldChangedAt(appointments[0], "status_id");
  const startedSince = changedAt?.getTime() ?? 0;
  const time = now - startedSince;

  return (
    <>
      <Box className="calendar-status" textAlign="center" color="#9C9C9C">
        <Box
          className="calendar-status--label"
          sx={{
            fontWeight: 500,
          }}
        >
          {f.minutesToDuration(msToM(time))}
        </Box>
        <LinearProgress
          className="calendar-status--progress"
          variant="determinate"
          sx={{ borderRadius: 2, backgroundColor: "#9C9C9C" }}
          value={0}
        />
      </Box>
      <ResourceExtraInfo
        appointments={appointments}
        setChangeEmployee={setChangeEmployee}
      />
    </>
  );
}

function Completion({ appointments, setChangeEmployee }) {
  const now = useNow();
  const appointment = appointments[0];

  const changedAt = getFieldChangedAt(
    appointment,
    "active_step",
    appointmentStep.COMPLETION,
  );
  const dur = realDuration(appointment);

  const startedSince = changedAt?.getTime() ?? 0;
  const expectedFinish = startedSince + dur;
  const remaining = expectedFinish - now;
  const time = now - startedSince;
  const progress = (time / dur) * 100;

  return (
    <>
      <Box
        className="calendar-status"
        textAlign="center"
        color={0 <= remaining ? "_components.heading.color" : "error.main"}
        sx={{
          "&:hover": {
            ".calendar-status--label": {
              display: "none",
            },
            ".calendar-status--progress": {
              display: "none",
            },
            ".calendar-status--label--hovered": {
              marginTop: "4px",
              display: "flex",
              justifyContent: "space-around",
            },
          },
        }}
      >
        <Box
          className="calendar-status--label--hovered"
          sx={{
            display: "none",
            fontWeight: 500,
          }}
        >
          <div>
            <FontAwesomeIcon icon={faChairOffice} color="primary" />
            &nbsp;
            {f.minutesToDuration(msToM(time))}
          </div>
          <div>
            <FontAwesomeIcon icon={faClock} color="primary" />
            &nbsp;
            {f.minutesToDuration(msToM(remaining))}
          </div>
        </Box>
        <Box
          className="calendar-status--label"
          sx={{
            fontWeight: 500,
          }}
        >
          {f.minutesToDuration(msToM(remaining < 0 ? time - dur : time))}
        </Box>
        <LinearProgress
          className="calendar-status--progress"
          variant="determinate"
          sx={{ borderRadius: 2 }}
          color={0 <= remaining ? "primary" : "error"}
          value={Math.min(100, progress)}
        />
      </Box>
      <ResourceExtraInfo
        appointments={appointments}
        setChangeEmployee={setChangeEmployee}
      />
    </>
  );
}

function ActiveStepSince({ appointments, setChangeEmployee }) {
  const now = useNow();
  const changedAt = getFieldChangedAt(appointments[0], "active_step");
  const startedSince = changedAt?.getTime() ?? 0;
  const time = now - startedSince;

  return (
    <>
      <Box className="calendar-status" textAlign="center" color="#9C9C9C">
        <Box
          className="calendar-status--label"
          sx={{
            fontWeight: 500,
          }}
        >
          {f.minutesToDuration(msToM(time))}
        </Box>
        <LinearProgress
          className="calendar-status--progress"
          variant="determinate"
          sx={{
            borderRadius: 2,
            backgroundColor: "#9C9C9C",
          }}
          value={0}
        />
      </Box>
      <ResourceExtraInfo
        appointments={appointments}
        setChangeEmployee={setChangeEmployee}
      />
    </>
  );
}

export default function CalendarStatus({ setChangeEmployee, appointments }) {
  const appointment = appointments[0];
  switch (appointment.active_step) {
    case appointmentStep.ASSIGNMENT:
      return (
        <StatusSince
          appointments={appointments}
          setChangeEmployee={setChangeEmployee}
        />
      );
    case appointmentStep.PREPARATION:
      return (
        <ActiveStepSince
          appointments={appointments}
          setChangeEmployee={setChangeEmployee}
        />
      );
    case appointmentStep.COMPLETION:
    case appointmentStep.RECEPTION_NOTES:
      return (
        <Completion
          appointments={appointments}
          setChangeEmployee={setChangeEmployee}
        />
      );
    case appointmentStep.PROCEDURE_NOTES:
      return (
        <ActiveStepSince
          appointments={appointments}
          setChangeEmployee={setChangeEmployee}
        />
      );
  }

  return (
    <>
      <Box className="calendar-status">
        <Box className="calendar-status--label" sx={{ fontWeight: 500 }}>
          &nbsp;
        </Box>
        <LinearProgress
          className="calendar-status--progress"
          variant="determinate"
          sx={{ borderRadius: 2 }}
          value={0}
        />
      </Box>
      <ResourceExtraInfo
        appointments={appointments}
        setChangeEmployee={setChangeEmployee}
      />
    </>
  );
}
